import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR, downloadFile } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { Plus, BellRinging, MagnifyingGlass, FileText, DownloadSimple, XCircle, Eye, UploadSimple, Copy, CheckCircle, CalendarBlank, ArrowsClockwise, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getToken, API } from "../lib/api";
import { JobMediaUploads } from "../components/JobMediaUploads";

const EMPTY = {
  partner_id: "", customer_id: "", customer_name: "", brand_id: "", product_category: "", product_type: "",
  service_type: "", part_name: "", amount: "", scheduled_date: "", address: "", notes: "",
};

export default function Jobs() {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [ratecards, setRatecards] = useState([]);
  const [brands, setBrands] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [assignJob, setAssignJob] = useState(null);
  const [assignPartnerId, setAssignPartnerId] = useState("");
  const [reassignJob, setReassignJob] = useState(null);
  const [reassignPartnerId, setReassignPartnerId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [docsJob, setDocsJob] = useState(null);
  const [invBusy, setInvBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [jobDocs, setJobDocs] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPartnerId, setBulkPartnerId] = useState("");
  const [cancelJob, setCancelJob] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [viewJob, setViewJob] = useState(null);
  const [completeJob, setCompleteJob] = useState(null);
  const [amtCollected, setAmtCollected] = useState("");
  const [completeBusy, setCompleteBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [cities, setCities] = useState([]);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkReasons, setBulkReasons] = useState({});
  const [bulkCancelBusy, setBulkCancelBusy] = useState(false);
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ scheduled_date: "", slot: "", remarks: "" });
  const [rescheduleBusy, setRescheduleBusy] = useState(false);

  const selectable = (j) => !["completed", "cancelled", "rejected"].includes(j.status);

  const openBulkCancel = () => {
    const init = {};
    Array.from(selected).forEach((id) => { init[id] = ""; });
    setBulkReasons(init); setBulkCancelOpen(true);
  };

  const doBulkCancel = async () => {
    const items = Array.from(selected).map((id) => ({ job_id: id, reason: (bulkReasons[id] || "").trim() }));
    if (items.some((it) => !it.reason)) { toast.error("Please enter a remark for every selected order"); return; }
    setBulkCancelBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk-cancel", { items });
      toast.success(`Cancelled ${data.cancelled} order(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setBulkCancelOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBulkCancelBusy(false); }
  };

  const openReschedule = (j) => { setRescheduleJob(j); setRescheduleForm({ scheduled_date: j.scheduled_date || "", slot: j.slot || "", remarks: "" }); };

  const doReschedule = async () => {
    if (!rescheduleForm.scheduled_date) { toast.error("Select a new date"); return; }
    setRescheduleBusy(true);
    try {
      const { data } = await api.post(`/jobs/${rescheduleJob.id}/reschedule`, rescheduleForm);
      toast.success("Appointment rescheduled");
      setRescheduleJob(null); if (viewJob?.id === data.id) setViewJob(data); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRescheduleBusy(false); }
  };

  const downloadB2BTemplate = async () => {
    try { await downloadFile("/jobs/b2b-import-template", "b2b_orders_import_template.xlsx"); }
    catch { toast.error("Could not download template"); }
  };

  const doB2BImport = async () => {
    if (!importFile) { toast.error("Choose an .xlsx file first"); return; }
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/jobs/b2b-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.created} order(s) imported${data.assigned ? `, ${data.assigned} auto-assigned` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setImportBusy(false); }
  };

  const assignable = (j) => j.status === "requested" || j.status === "rejected";
  const reassignable = (j) => !!j.partner_id && ["assigned", "accepted", "in_progress", "rejected"].includes(j.status);
  const cancellable = (j) => ["requested", "assigned", "accepted", "in_progress"].includes(j.status);

  const doCancel = async () => {
    if (!cancelReason.trim()) { toast.error("A cancellation reason is required"); return; }
    setCancelBusy(true);
    try {
      await api.post(`/jobs/${cancelJob.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Booking cancelled & partner notified");
      setCancelJob(null); setCancelReason("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCancelBusy(false); }
  };

  const doReopen = async (j) => {
    if (!window.confirm("Reopen this cancelled job and re-assign to its partner?")) return;
    try {
      const { data } = await api.post(`/jobs/${j.id}/reopen`);
      toast.success(`Job reopened (${data.status})`);
      setViewJob(data); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const scheduleLink = (j) => `${window.location.origin}/schedule?id=${encodeURIComponent(j.b2b_partner_order_id || j.booking_code || j.id)}`;

  const saveRouting = async (v) => {
    try {
      const { data } = await api.put(`/jobs/${viewJob.id}/additional-charges-routing`, { payment_to: v });
      setViewJob(data); load();
      toast.success(`Additional charges routed to ${v === "partner" ? "Partner" : "Fixigo"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };


  const doComplete = async () => {
    setCompleteBusy(true);
    try {
      const { data } = await api.post(`/jobs/${completeJob.id}/complete`, { amount_collected: Number(amtCollected) || 0 });
      toast.success(`Job marked completed · ₹${Number(amtCollected) || 0} recorded`);
      setCompleteJob(null); setAmtCollected(""); setViewJob(data); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCompleteBusy(false); }
  };

  const copyId = (id) => { navigator.clipboard.writeText(id); toast.success("Booking ID copied"); };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = (ids) => setSelected((s) => {
    const allSel = ids.length > 0 && ids.every((id) => s.has(id));
    return allSel ? new Set() : new Set(ids);
  });

  const doBulkAssign = async () => {
    if (!bulkPartnerId) { toast.error("Select a partner"); return; }
    try {
      const r = await api.post("/jobs/bulk-assign", { job_ids: Array.from(selected), partner_id: bulkPartnerId });
      toast.success(`Assigned ${r.data.assigned} job(s)${r.data.skipped ? `, ${r.data.skipped} skipped (already assigned)` : ""}`);
      setBulkOpen(false); setBulkPartnerId(""); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportJobs = async () => {
    try {
      await downloadFile("/jobs/export", "fixigo_jobs.xlsx", {
        status: statusFilter === "all" ? "" : statusFilter,
        partner_id: partnerFilter === "all" ? "" : partnerFilter,
      });
    } catch { toast.error("Export failed"); }
  };

  const load = useCallback(() => {
    api.get("/jobs", { params: {
      status: statusFilter === "all" ? "" : statusFilter,
      partner_id: partnerFilter === "all" ? "" : partnerFilter,
      city: cityFilter === "all" ? "" : cityFilter,
      q: search.trim(),
    } }).then((r) => setRows(r.data));
  }, [statusFilter, partnerFilter, cityFilter, search]);

  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/partners", { params: { status: "active" } }).then((r) => setPartners(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/ratecards", { params: { status: "active" } }).then((r) => setRatecards(r.data));
    api.get("/document-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/jobs/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/service-brands", { params: { active: "1" } }).then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY); setOpen(true); };

  const pickRatecard = (rid) => {
    const rc = ratecards.find((r) => r.id === rid);
    if (rc) setForm((f) => ({ ...f, product_category: rc.product_category, product_type: rc.product_type,
      service_type: rc.service_type, part_name: rc.part_name, amount: rc.amount }));
  };

  const save = async () => {
    if (!form.partner_id || !form.product_category || !form.service_type) {
      toast.error("Partner, Category and Service Type are required");
      return;
    }
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      await api.post("/jobs", payload);
      toast.success("Job assigned & partner notified");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remind = async (jid) => {
    try {
      await api.post(`/jobs/${jid}/remind`);
      toast.success("Reminder sent to partner");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doAssign = async () => {
    if (!assignPartnerId) { toast.error("Select a partner"); return; }
    try {
      await api.post(`/jobs/${assignJob.id}/assign`, { partner_id: assignPartnerId });
      toast.success("Partner assigned & notified");
      setAssignJob(null);
      setAssignPartnerId("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doReassign = async () => {
    if (!reassignPartnerId) { toast.error("Select a partner"); return; }
    try {
      await api.post(`/jobs/${reassignJob.id}/reassign`, { partner_id: reassignPartnerId, reason: reassignReason });
      toast.success("Job reassigned & partners notified");
      setReassignJob(null); setReassignPartnerId(""); setReassignReason("");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openDocs = (j) => {
    setDocsJob(j); setSelectedTpl("");
    api.get("/generated-documents", { params: { job_id: j.id } }).then((r) => setJobDocs(r.data)).catch(() => setJobDocs([]));
  };

  const generate = async () => {
    if (!selectedTpl) { toast.error("Select a template"); return; }
    setGenBusy(true);
    try {
      await api.post(`/jobs/${docsJob.id}/generate-document`, { template_id: selectedTpl });
      toast.success("Document generated");
      const r = await api.get("/generated-documents", { params: { job_id: docsJob.id } });
      setJobDocs(r.data);
      setSelectedTpl("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGenBusy(false); }
  };

  const genUrl = (g) => `${API}/generated-documents/${g.id}/download?auth=${getToken()}`;
  const invoiceUrl = () => `${API}/jobs/${docsJob.id}/invoice?auth=${getToken()}`;

  const uploadInvoice = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !docsJob) return;
    setInvBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/jobs/${docsJob.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Invoice uploaded — now visible to the customer");
      setDocsJob({ ...docsJob, invoice_name: data.invoice_name });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setInvBusy(false); }
  };

  const removeInvoice = async () => {
    if (!window.confirm("Remove this invoice? The customer will no longer see it.")) return;
    setInvBusy(true);
    try {
      await api.delete(`/jobs/${docsJob.id}/invoice`);
      toast.success("Invoice removed");
      setDocsJob({ ...docsJob, invoice_name: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setInvBusy(false); }
  };

  return (
    <>
      <PageHeader title="Jobs" subtitle="Assign & track partner jobs"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} data-testid="import-b2b-btn">
              <UploadSimple size={15} weight="bold" /> Import B2B Orders
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={exportJobs} data-testid="export-jobs-btn">
              <DownloadSimple size={15} weight="bold" /> Export Excel
            </Button>
            <Button data-testid="add-job-btn" onClick={openNew} className="rounded-sm gap-2"><Plus size={16} weight="bold" /> Assign Job</Button>
          </div>
        } />
      <div className="p-8 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search mobile / booking id / partner..."
              className="rounded-sm w-72 pl-9" data-testid="job-search" />
          </div>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="rounded-sm w-44" data-testid="job-city-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={partnerFilter} onValueChange={setPartnerFilter}>
            <SelectTrigger className="rounded-sm w-52" data-testid="job-partner-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Partners</SelectItem>
              {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm w-44" data-testid="job-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "requested", "assigned", "accepted", "in_progress", "completed", "rejected", "cancelled"].map((s) =>
                <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All Status" : s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {selected.size > 0 && (
          <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-sm px-4 py-2.5" data-testid="bulk-action-bar">
            <span className="text-sm font-semibold text-primary">{selected.size} job(s) selected</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setSelected(new Set())}>Clear</Button>
              <Button size="sm" className="rounded-sm gap-2" onClick={() => { setBulkPartnerId(""); setBulkOpen(true); }} data-testid="bulk-assign-btn">
                <Plus size={15} weight="bold" /> Assign to Partner
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm gap-2 text-destructive border-destructive/30 hover:bg-destructive/5" onClick={openBulkCancel} data-testid="bulk-cancel-btn">
                <XCircle size={15} weight="bold" /> Cancel Selected
              </Button>
            </div>
          </div>
        )}

        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="w-10">
                  <input type="checkbox" data-testid="select-all-jobs"
                    checked={rows.filter(selectable).length > 0 && rows.filter(selectable).every((j) => selected.has(j.id))}
                    onChange={() => toggleSelAll(rows.filter(selectable).map((j) => j.id))} />
                </TableHead>
                <TableHead>Order Id</TableHead><TableHead>Booking Id</TableHead>
                <TableHead>Customer</TableHead><TableHead>Contact No</TableHead>
                <TableHead>Pincode</TableHead><TableHead>City</TableHead>
                <TableHead>Partner</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="jobs-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">No jobs</TableCell></TableRow>}
              {rows.map((j) => (
                <TableRow key={j.id} className="hover:bg-secondary/30">
                  <TableCell>
                    {selectable(j) && (
                      <input type="checkbox" data-testid={`select-job-${j.id}`} checked={selected.has(j.id)} onChange={() => toggleSel(j.id)} />
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-mono">{j.b2b_partner_order_id || "—"}{j.b2b && <span className="ml-1.5 text-[10px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded" data-testid={`b2b-tag-${j.id}`}>B2B</span>}</TableCell>
                  <TableCell className="font-mono font-semibold text-primary" data-testid={`booking-code-${j.id}`}>{j.booking_code || "—"}</TableCell>
                  <TableCell>{j.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{j.customer_phone || "—"}</TableCell>
                  <TableCell className="text-sm">{j.pincode || "—"}</TableCell>
                  <TableCell className="text-sm">{j.city || "—"}</TableCell>
                  <TableCell className="font-medium">{j.partner_name || <span className="text-muted-foreground font-normal">Unassigned</span>}</TableCell>
                  <TableCell><StatusBadge value={j.status === "in_progress" ? "pending" : (j.status === "completed" ? "complete" : j.status)} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 text-primary" onClick={() => setViewJob(j)} data-testid={`view-job-${j.id}`}>
                      <Eye size={15} /> View
                    </Button>
                    {j.status === "requested" && (
                      <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 text-primary" onClick={() => { setAssignJob(j); setAssignPartnerId(""); }} data-testid={`assign-${j.id}`}>
                        <Plus size={15} /> Assign Partner
                      </Button>
                    )}
                    {j.status === "assigned" && (
                      <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 text-amber-700" onClick={() => remind(j.id)} data-testid={`remind-${j.id}`}>
                        <BellRinging size={15} /> Remind
                      </Button>
                    )}
                    {reassignable(j) && (
                      <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 text-indigo-700" onClick={() => { setReassignJob(j); setReassignPartnerId(""); setReassignReason(""); }} data-testid={`reassign-${j.id}`}>
                        <ArrowsClockwise size={15} /> Reassign
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="rounded-sm gap-1.5" onClick={() => openDocs(j)} data-testid={`docs-${j.id}`}>
                      <FileText size={15} /> Docs
                    </Button>
                    {cancellable(j) && (
                      <Button variant="ghost" size="sm" className="rounded-sm gap-1.5 text-destructive" onClick={() => { setCancelJob(j); setCancelReason(""); }} data-testid={`cancel-job-${j.id}`}>
                        <XCircle size={15} /> Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="job-form">
          <SheetHeader><SheetTitle className="font-heading">Assign New Job</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div>
              <Label className="label-caps">Partner</Label>
              <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="job-field-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Pick from Ratecard (auto-fills)</Label>
              <Select onValueChange={pickRatecard}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="job-field-ratecard"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {ratecards.map((r) => <SelectItem key={r.id} value={r.id}>{r.product_category} · {r.product_type} · {r.service_type} — {fmtINR(r.amount)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => {
                const c = customers.find((x) => x.id === v);
                setForm({ ...form, customer_id: v, customer_name: c ? c.name : "" });
              }}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="job-field-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Category</Label>
                <Input data-testid="job-field-category" value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })} className="rounded-sm mt-1.5" /></div>
              <div><Label className="label-caps">Product Type</Label>
                <Input value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} className="rounded-sm mt-1.5" /></div>
            </div>
            <div>
              <Label className="label-caps">Brand <span className="text-muted-foreground normal-case">(drives required media uploads)</span></Label>
              <Select value={form.brand_id || "__none__"} onValueChange={(v) => setForm({ ...form, brand_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="job-field-brand"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No brand</SelectItem>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Service Type</Label>
                <Input data-testid="job-field-service" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="rounded-sm mt-1.5" /></div>
              <div><Label className="label-caps">Amount (₹)</Label>
                <Input data-testid="job-field-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm mt-1.5" /></div>
            </div>
            <div>
              <Label className="label-caps">Scheduled Date</Label>
              <Input data-testid="job-field-date" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="label-caps">Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm mt-1.5" />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="job-save">Assign & Notify</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!assignJob} onOpenChange={(o) => !o && setAssignJob(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="assign-form">
          <SheetHeader><SheetTitle className="font-heading">Assign Partner</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            {assignJob && (
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{assignJob.service_type} · {assignJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{assignJob.customer_name} · {assignJob.address || "no address"} · {fmtINR(assignJob.amount)}</p>
              </div>
            )}
            <div>
              <Label className="label-caps">Partner</Label>
              <Select value={assignPartnerId} onValueChange={setAssignPartnerId}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="assign-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAssignJob(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={doAssign} data-testid="assign-save">Assign & Notify</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!reassignJob} onOpenChange={(o) => !o && setReassignJob(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="reassign-form">
          <SheetHeader><SheetTitle className="font-heading">Reassign Job</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            {reassignJob && (
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{reassignJob.booking_code || reassignJob.service_type} · {reassignJob.customer_name || "—"}</p>
                <p className="text-xs text-muted-foreground">Currently with: <span className="font-medium">{reassignJob.partner_name || "—"}</span></p>
              </div>
            )}
            <div>
              <Label className="label-caps">New Partner</Label>
              <Select value={reassignPartnerId} onValueChange={setReassignPartnerId}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="reassign-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>
                  {partners.filter((p) => p.id !== reassignJob?.partner_id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Reason (optional)</Label>
              <Textarea value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} placeholder="e.g. Partner unavailable, out of coverage…" className="rounded-sm mt-1.5" data-testid="reassign-reason" />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setReassignJob(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={doReassign} data-testid="reassign-save">Reassign & Notify</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!docsJob} onOpenChange={(o) => !o && setDocsJob(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="docs-form">
          <SheetHeader><SheetTitle className="font-heading">Documents</SheetTitle></SheetHeader>
          <div className="space-y-5 py-6">
            {docsJob && (
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{docsJob.service_type} · {docsJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{docsJob.customer_name || "—"} · {docsJob.partner_name || "Unassigned"} · {fmtINR(docsJob.amount)}</p>
              </div>
            )}
            <div>
              <Label className="label-caps">Generate from template</Label>
              <div className="flex gap-2 mt-1.5">
                <Select value={selectedTpl} onValueChange={setSelectedTpl}>
                  <SelectTrigger className="rounded-sm" data-testid="docs-template-select"><SelectValue placeholder={templates.length ? "Select template" : "No templates uploaded"} /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.file_format})</SelectItem>)}</SelectContent>
                </Select>
                <Button className="rounded-sm shrink-0" onClick={generate} disabled={genBusy || !templates.length} data-testid="docs-generate">{genBusy ? "…" : "Generate"}</Button>
              </div>
              {!templates.length && <p className="text-xs text-muted-foreground mt-2">Upload templates from the Documents page first.</p>}
            </div>
            <div>
              <Label className="label-caps">Generated documents</Label>
              <div className="mt-2 space-y-2" data-testid="job-docs-list">
                {jobDocs.length === 0 && <p className="text-xs text-muted-foreground">None yet</p>}
                {jobDocs.map((g) => (
                  <div key={g.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{g.template_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{g.filename}</p>
                    </div>
                    <a href={genUrl(g)} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="sm" className="rounded-sm" data-testid={`job-doc-download-${g.id}`}><DownloadSimple size={15} /></Button>
                    </a>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-caps">Customer Invoice</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Upload the invoice PDF you created in your accounting book. It appears in the customer's portal under Invoices for this booking.</p>
              {docsJob?.invoice_name ? (
                <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm mt-2" data-testid="job-invoice-current">
                  <a href={invoiceUrl()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary font-medium min-w-0">
                    <FileText size={15} className="shrink-0" /> <span className="truncate">{docsJob.invoice_name}</span>
                  </a>
                  <Button variant="ghost" size="sm" className="rounded-sm text-destructive shrink-0" onClick={removeInvoice} disabled={invBusy} data-testid="job-invoice-remove"><XCircle size={15} /></Button>
                </div>
              ) : (
                <label className="mt-2 block">
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={uploadInvoice} data-testid="job-invoice-input" />
                  <Button asChild variant="outline" className="rounded-sm gap-1.5 w-full" disabled={invBusy}>
                    <span className="cursor-pointer" data-testid="job-invoice-upload"><UploadSimple size={15} /> {invBusy ? "Uploading…" : "Upload Invoice"}</span>
                  </Button>
                </label>
              )}
            </div>
            <div className="border-t border-border pt-4">
              <Label className="label-caps">Brand-required Media</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">Photos/videos/documents required for this job's brand &amp; category. Required items must be uploaded before the job can be completed.</p>
              {docsJob && <JobMediaUploads jobId={docsJob.id} canUpload />}
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={bulkOpen} onOpenChange={setBulkOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="bulk-assign-form">
          <SheetHeader><SheetTitle className="font-heading">Bulk Assign Partner</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <p className="text-sm text-muted-foreground">Assigning <strong>{selected.size}</strong> selected job(s) to one partner. The partner is notified by email and in-app.</p>
            <div>
              <Label className="label-caps">Partner</Label>
              <Select value={bulkPartnerId} onValueChange={setBulkPartnerId}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="bulk-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                <SelectContent>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={doBulkAssign} data-testid="bulk-assign-save">Assign {selected.size} job(s)</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={!!cancelJob} onOpenChange={(o) => !o && setCancelJob(null)}>
        <DialogContent data-testid="job-cancel-dialog">
          <DialogHeader><DialogTitle className="font-heading">Cancel booking</DialogTitle></DialogHeader>
          {cancelJob && (
            <div className="space-y-4 py-2">
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{cancelJob.service_type} · {cancelJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{cancelJob.customer_name || "—"} · {cancelJob.partner_name || "Unassigned"}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                The assigned partner (if any) will be notified by email and in-app. This is logged with your name, role, reason and time.
              </div>
              <div>
                <Label className="label-caps">Reason for cancellation *</Label>
                <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="rounded-sm mt-1.5"
                  placeholder="e.g. Customer requested, duplicate booking, partner unavailable" data-testid="job-cancel-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCancelJob(null)}>Keep booking</Button>
            <Button className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doCancel} disabled={cancelBusy} data-testid="job-confirm-cancel">
              {cancelBusy ? "Cancelling…" : "Cancel Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="b2b-import-dialog">
          <DialogHeader><DialogTitle className="font-heading">Import B2B Orders</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Upload an .xlsx of B2B service orders. Each row becomes a booking with a custom <strong>Customer Price</strong> and <strong>Partner Price</strong>; Fixigo Margin = Customer − Partner is auto-calculated. Set <strong>Payment Collection</strong> to <em>brand</em> (already paid, no balance) or <em>customer</em> (customer pays via their login — default). Customers are emailed their service request automatically, and rows with a valid pincode are auto-assigned to a covering partner.</p>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={downloadB2BTemplate} data-testid="b2b-template-btn">
              <DownloadSimple size={15} weight="bold" /> Download Excel Template
            </Button>
            <div>
              <Label className="label-caps">Excel file (.xlsx)</Label>
              <Input type="file" accept=".xlsx" className="rounded-sm mt-1.5" data-testid="b2b-file-input"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} />
            </div>
            {importResult && (
              <div className="bg-secondary rounded-sm p-3 text-sm space-y-1" data-testid="b2b-import-result">
                <p className="font-semibold text-green-700">{importResult.created} order(s) imported · {importResult.assigned} auto-assigned · {importResult.skipped} skipped</p>
                {importResult.errors?.length > 0 && (
                  <div className="text-xs text-destructive max-h-32 overflow-y-auto">
                    {importResult.errors.map((er, i) => <div key={i}>{er}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setImportOpen(false)}>Close</Button>
            <Button className="rounded-sm gap-2" onClick={doB2BImport} disabled={importBusy || !importFile} data-testid="b2b-import-submit">
              <UploadSimple size={15} weight="bold" /> {importBusy ? "Importing…" : "Import Orders"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!viewJob} onOpenChange={(o) => !o && setViewJob(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="job-view-panel">
          <SheetHeader>
            <SheetTitle className="font-heading">Booking Details</SheetTitle>
            <SheetDescription>Customer, service and payment information for this booking.</SheetDescription>
          </SheetHeader>
          {viewJob && (
            <div className="py-5">
              <div className="flex items-center justify-between mb-4">
                <div className="min-w-0">
                  <p className="font-semibold text-base">{viewJob.service_type} · {viewJob.product_category}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-sm font-mono font-bold text-primary" data-testid="view-booking-id">{viewJob.booking_code || viewJob.id}</span>
                    <button onClick={() => copyId(viewJob.booking_code || viewJob.id)} className="text-primary hover:opacity-70 shrink-0" title="Copy Booking ID" data-testid="copy-booking-id"><Copy size={13} /></button>
                    {viewJob.b2b_partner_order_id && <span className="text-xs text-muted-foreground">· Order {viewJob.b2b_partner_order_id}</span>}
                  </div>
                </div>
                <StatusBadge value={viewJob.status === "in_progress" ? "pending" : (viewJob.status === "completed" ? "complete" : viewJob.status)} />
              </div>
              <Tabs defaultValue="customer">
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="customer" data-testid="view-tab-customer">Customer</TabsTrigger>
                  <TabsTrigger value="service" data-testid="view-tab-service">Service</TabsTrigger>
                  <TabsTrigger value="payment" data-testid="view-tab-payment">Payment</TabsTrigger>
                </TabsList>

                <TabsContent value="customer" className="mt-4" data-testid="view-customer-content">
                  <div className="border border-border rounded-sm divide-y divide-border">
                    <Row label="Name" value={viewJob.customer_name} />
                    <Row label="Email" value={viewJob.customer_email} />
                    <Row label="Phone" value={viewJob.customer_phone} />
                    <Row label="Address" value={viewJob.address} />
                    <Row label="City" value={viewJob.city} />
                    <Row label="State" value={viewJob.state} />
                    <Row label="Pincode" value={viewJob.pincode} />
                  </div>
                </TabsContent>

                <TabsContent value="service" className="mt-4" data-testid="view-service-content">
                  <div className="mb-3 flex flex-wrap items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-sm p-3" data-testid="view-schedule-link-box">
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Customer Scheduling Link</p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-all">{scheduleLink(viewJob)}</p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-sm gap-1.5" data-testid="view-copy-schedule-link"
                      onClick={() => { navigator.clipboard.writeText(scheduleLink(viewJob)); toast.success("Scheduling link copied"); }}>
                      <Copy size={14} /> Copy Link
                    </Button>
                    <a href={scheduleLink(viewJob)} target="_blank" rel="noreferrer" data-testid="view-open-schedule-link">
                      <Button size="sm" variant="outline" className="rounded-sm gap-1.5"><ArrowSquareOut size={14} /> Open</Button>
                    </a>
                  </div>
                  <div className="border border-border rounded-sm divide-y divide-border">
                    <Row label="Category" value={viewJob.product_category} />
                    <Row label="Brand" value={viewJob.brand_name} />
                    <Row label="Product Type" value={viewJob.product_type} />
                    <Row label="Service" value={viewJob.service_type} />
                    <Row label="Part / Spare" value={viewJob.part_name} />
                    <Row label="Issue" value={viewJob.issue || viewJob.notes} />
                    <Row label="Preferred Date" value={viewJob.scheduled_date} />
                    <Row label="Time Slot" value={viewJob.slot} highlight />
                    <Row label="Partner" value={viewJob.partner_name || "Unassigned"} />
                  </div>
                  {(viewJob.preferred_service_date || viewJob.maps_location || viewJob.customer_remarks) && (
                    <div className="border border-border rounded-sm divide-y divide-border mt-3" data-testid="view-customer-schedule">
                      <div className="px-3 py-2 bg-secondary/50 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Customer-submitted schedule</div>
                      <Row label="Preferred Date (customer)" value={viewJob.preferred_service_date} />
                      <Row label="Preferred Slot (customer)" value={viewJob.preferred_slot} />
                      <Row label="Remarks" value={viewJob.customer_remarks} />
                      <Row label="Location" value={viewJob.maps_location
                        ? <a href={viewJob.maps_location} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline" data-testid="view-open-location">Open Location ↗</a>
                        : "—"} />
                      <Row label="Submitted At" value={fmtDT(viewJob.schedule_submitted_at)} />
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="payment" className="mt-4" data-testid="view-payment-content">
                  <div className="border border-border rounded-sm divide-y divide-border">
                    {viewJob.b2b && (
                      <>
                        <Row label="Order Type" value={<span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">B2B</span>} />
                        <Row label="B2B Order Id" value={viewJob.b2b_partner_order_id} mono />
                        <Row label="Order Reference" value={viewJob.b2b_partner_name} />
                        <Row label="Customer Price" value={fmtINR(viewJob.amount)} />
                        <Row label="Partner Price" value={viewJob.partner_rate != null ? fmtINR(viewJob.partner_rate) : "—"} />
                        <Row label="Fixigo Margin" value={viewJob.partner_rate != null ? fmtINR(Math.round((viewJob.amount - viewJob.partner_rate) * 100) / 100) : "—"} highlight />
                      </>
                    )}
                    <Row label="Amount" value={fmtINR(viewJob.amount)} />
                    <Row label="Payment Mode" value={viewJob.payment_mode === "online" ? "Online (Razorpay)" : (viewJob.payment_mode === "after" ? "Pay after service" : (viewJob.payment_mode === "partial" ? "Partial (booking amount)" : (viewJob.payment_mode === "full" ? "Full (online)" : (viewJob.payment_mode === "b2b_collect" ? "B2B (collect from customer)" : (viewJob.payment_mode === "b2b_prepaid" ? "B2B (prepaid by brand)" : (viewJob.payment_mode === "b2b" ? "B2B (billed to partner)" : viewJob.payment_mode))))))} />
                    <Row label="Payment Status" value={<PayBadge status={viewJob.payment_status} />} />
                    <Row label="Amount Paid" value={viewJob.paid_amount != null ? fmtINR(viewJob.paid_amount) : "—"} />
                    <Row label="Balance Due" value={viewJob.balance_due != null ? fmtINR(viewJob.balance_due) : "—"} highlight={viewJob.balance_due > 0} />
                    {viewJob.completion_collected != null && viewJob.completion_collected > 0 && (
                      <Row label="Collected on Completion" value={fmtINR(viewJob.completion_collected)} />
                    )}
                    <Row label="Paid At" value={fmtDT(viewJob.paid_at)} />
                    <Row label="Razorpay Order ID" value={viewJob.razorpay_order_id} mono />
                    <Row label="Razorpay Payment ID" value={viewJob.razorpay_payment_id} mono />
                    {viewJob.refund && (
                      <>
                        <Row label="Refund ID" value={viewJob.refund.refund_id} mono />
                        <Row label="Refund Amount" value={viewJob.refund.amount != null ? fmtINR(viewJob.refund.amount) : "—"} />
                        <Row label="Refund Status" value={<PayBadge status={viewJob.refund.status} />} />
                      </>
                    )}
                  </div>
                  <div className="mt-4 border border-border rounded-sm p-3" data-testid="view-addcharge-routing">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">Additional Charges Payment To</p>
                        <p className="text-xs text-muted-foreground">Who collects extra/spare charges. "Partner" = Razorpay collects only the standard charge; partner collects the rest.</p>
                      </div>
                      <Select value={viewJob.additional_charges_payment_to || "fixigo"} onValueChange={saveRouting}>
                        <SelectTrigger className="w-36 rounded-sm" data-testid="view-routing-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixigo">Fixigo</SelectItem>
                          <SelectItem value="partner">Partner</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {viewJob.additional_charges_payment_to === "partner" && (
                      <p className="text-xs mt-2 text-muted-foreground">Additional charges status: <strong className="capitalize text-foreground">{viewJob.additional_charges_payment_status || "pending"}</strong>{viewJob.additional_charges_received_by ? ` · confirmed by ${viewJob.additional_charges_received_by}` : ""}</p>
                    )}
                  </div>
                  {viewJob.balance_due > 0 && (
                    <a href={`/collect-payment?bid=${viewJob.id}`} data-testid="view-collect-balance"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                      → Collect balance {fmtINR(viewJob.balance_due)} (send payment link)
                    </a>
                  )}
                  {!viewJob.payment_status && (
                    <p className="text-xs text-muted-foreground mt-3">This booking predates online payments (older or manually-created booking).</p>
                  )}
                </TabsContent>
              </Tabs>

              {(viewJob.addons || []).length > 0 && (
                <div className="mt-5" data-testid="view-addons">
                  <p className="label-caps mb-2">Additional Items (partner add-ons)</p>
                  <div className="border border-border rounded-sm divide-y divide-border">
                    {viewJob.addons.map((a) => (
                      <div key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div><span className="font-medium">{a.name}</span> <span className="text-xs text-muted-foreground">× {a.qty}</span></div>
                        <div className="text-right">
                          <span className="font-mono">{fmtINR(a.total)}</span>
                          <span className={`ml-2 text-xs font-semibold ${a.status === "accepted" ? "text-green-600" : a.status === "rejected" ? "text-destructive" : "text-amber-600"}`}>{a.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-6">
                {!["completed", "cancelled", "rejected"].includes(viewJob.status) && (
                  <Button className="rounded-sm gap-1.5 bg-green-600 hover:bg-green-700" onClick={() => { setCompleteJob(viewJob); setAmtCollected(String(viewJob.balance_due || viewJob.amount || "")); }} data-testid="view-mark-completed">
                    <CheckCircle size={15} weight="bold" /> Mark Completed
                  </Button>
                )}
                {!["completed", "cancelled", "rejected"].includes(viewJob.status) && (
                  <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => openReschedule(viewJob)} data-testid="view-reschedule">
                    <CalendarBlank size={15} weight="bold" /> Reschedule
                  </Button>
                )}
                {viewJob.status === "cancelled" && (
                  <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => doReopen(viewJob)} data-testid="view-reopen">
                    <BellRinging size={15} weight="bold" /> Reopen Job
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!completeJob} onOpenChange={(o) => !o && setCompleteJob(null)}>
        <DialogContent data-testid="complete-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mark job as completed</DialogTitle></DialogHeader>
          {completeJob && (
            <div className="space-y-4 py-2">
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{completeJob.service_type} · {completeJob.product_category}</p>
                <p className="text-xs text-muted-foreground">{completeJob.customer_name} · Balance due {fmtINR(completeJob.balance_due || 0)}</p>
              </div>
              <div>
                <Label className="label-caps">Amount collected from customer (₹)</Label>
                <Input type="number" min={0} value={amtCollected} onChange={(e) => setAmtCollected(e.target.value)} className="rounded-sm mt-1.5" data-testid="complete-amount-input" />
                <p className="text-xs text-muted-foreground mt-1.5">This is recorded as collected and the booking balance is marked settled/paid.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCompleteJob(null)}>Cancel</Button>
            <Button className="rounded-sm bg-green-600 hover:bg-green-700" onClick={doComplete} disabled={completeBusy} data-testid="complete-confirm">{completeBusy ? "Saving…" : "Confirm Completion"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkCancelOpen} onOpenChange={setBulkCancelOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-cancel-dialog">
          <DialogHeader><DialogTitle className="font-heading">Cancel {selected.size} order(s)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[55vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground">Enter a cancellation remark for each order. Assigned partners will be notified.</p>
            {rows.filter((j) => selected.has(j.id)).map((j) => (
              <div key={j.id} className="border border-border rounded-sm p-3" data-testid={`bulk-cancel-row-${j.id}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-mono font-semibold text-primary">{j.booking_code || j.id.slice(0, 8)}</span>
                  <span className="text-muted-foreground">{j.customer_name} · {j.service_type}</span>
                </div>
                <Input value={bulkReasons[j.id] || ""} onChange={(e) => setBulkReasons((r) => ({ ...r, [j.id]: e.target.value }))}
                  placeholder="Cancellation remark…" className="rounded-sm h-9" data-testid={`bulk-cancel-reason-${j.id}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setBulkCancelOpen(false)}>Back</Button>
            <Button className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doBulkCancel} disabled={bulkCancelBusy} data-testid="bulk-cancel-confirm">{bulkCancelBusy ? "Cancelling…" : "Cancel Orders"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rescheduleJob} onOpenChange={(o) => !o && setRescheduleJob(null)}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reschedule appointment</DialogTitle></DialogHeader>
          {rescheduleJob && (
            <div className="space-y-4 py-2">
              <div className="bg-secondary rounded-sm p-3 text-sm">
                <p className="font-semibold">{rescheduleJob.booking_code} · {rescheduleJob.service_type}</p>
                <p className="text-xs text-muted-foreground">{rescheduleJob.customer_name} · Currently {rescheduleJob.scheduled_date || "—"} {rescheduleJob.slot}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">New Date</Label>
                  <Input type="date" value={rescheduleForm.scheduled_date} onChange={(e) => setRescheduleForm({ ...rescheduleForm, scheduled_date: e.target.value })} className="rounded-sm mt-1.5" data-testid="reschedule-date" /></div>
                <div><Label className="label-caps">Time Slot</Label>
                  <Input value={rescheduleForm.slot} onChange={(e) => setRescheduleForm({ ...rescheduleForm, slot: e.target.value })} placeholder="e.g. 2:00 PM - 4:00 PM" className="rounded-sm mt-1.5" data-testid="reschedule-slot" /></div>
              </div>
              <div><Label className="label-caps">Remarks</Label>
                <Input value={rescheduleForm.remarks} onChange={(e) => setRescheduleForm({ ...rescheduleForm, remarks: e.target.value })} placeholder="Reason / note for rescheduling" className="rounded-sm mt-1.5" data-testid="reschedule-remarks" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRescheduleJob(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={doReschedule} disabled={rescheduleBusy} data-testid="reschedule-confirm">{rescheduleBusy ? "Saving…" : "Reschedule"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Row = ({ label, value, mono, highlight }) => (
  <div className="flex items-start justify-between gap-4 px-3 py-2.5">
    <span className="text-xs uppercase tracking-wide text-muted-foreground shrink-0">{label}</span>
    <span className={`text-sm text-right break-all ${mono ? "font-mono text-xs" : ""} ${highlight ? "font-semibold text-primary" : ""}`}>
      {value == null || value === "" ? <span className="text-muted-foreground">—</span> : value}
    </span>
  </div>
);

const PayBadge = ({ status }) => {
  const map = {
    paid: "bg-green-100 text-green-700",
    partial: "bg-indigo-100 text-indigo-700",
    pay_after: "bg-amber-100 text-amber-700",
    b2b: "bg-indigo-100 text-indigo-700",
    pending: "bg-amber-100 text-amber-700",
    refunded: "bg-blue-100 text-blue-700",
    refund_failed: "bg-red-100 text-red-700",
    failed: "bg-red-100 text-red-700",
    processed: "bg-blue-100 text-blue-700",
  };
  if (!status) return <span className="text-muted-foreground">—</span>;
  return <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-semibold capitalize ${map[status] || "bg-secondary text-foreground"}`}>{status.replace("_", " ")}</span>;
};

const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};
